/**
 * @author bai <ghimping@alpstein.my>
 * @link https://www.alpstein.my
 * @copyright Copyright (c) Alpstein Software
 *           _____                    _____                    _____
 *          /\    \                  /\    \                  /\    \
 *         /::\    \                /::\    \                /::\    \
 *        /::::\    \              /::::\    \               \:::\    \
 *       /::::::\    \            /::::::\    \               \:::\    \
 *      /:::/\:::\    \          /:::/\:::\    \               \:::\    \
 *     /:::/__\:::\    \        /:::/__\:::\    \               \:::\    \
 *    /::::\   \:::\    \      /::::\   \:::\    \              /::::\    \
 *   /::::::\   \:::\    \    /::::::\   \:::\    \    ____    /::::::\    \
 *  /:::/\:::\   \:::\ ___\  /:::/\:::\   \:::\    \  /\   \  /:::/\:::\    \
 * /:::/__\:::\   \:::|    |/:::/  \:::\   \:::\____\/::\   \/:::/  \:::\____\
 * \:::\   \:::\  /:::|____|\::/    \:::\  /:::/    /\:::\  /:::/    \::/    /
 *  \:::\   \:::\/:::/    /  \/____/ \:::\/:::/    /  \:::\/:::/    / \/____/
 *   \:::\   \::::::/    /            \::::::/    /    \::::::/    /
 *    \:::\   \::::/    /              \::::/    /      \::::/____/
 *     \:::\  /:::/    /               /:::/    /        \:::\    \
 *      \:::\/:::/    /               /:::/    /          \:::\    \
 *       \::::::/    /               /:::/    /            \:::\    \
 *        \::::/    /               /:::/    /              \:::\____\
 *         \::/____/                \::/    /                \::/    /
 *          ~~                       \/____/                  \/____/
 */

import apiInstance, { AUTH_TOKEN_HEADER } from "./httpManager";
import _ from "underscore";

/**
 * To update api authorization header
 * @param token
 */
const setHeaderToken = token => {
  apiInstance.defaults.headers[AUTH_TOKEN_HEADER] = token;
};

/**
 * To update language in api header
 * @param locale
 */
const setHeaderLanguage = locale => {
  apiInstance.defaults.headers["Accept-Language"] = locale;
};

/**
 * To register new account
 * @param data
 * @param referrerCode [OPTIONAL]
 */
const signUpAccount = (data, referrerCode) => {
  const params = referrerCode ? { ...data, referrer_code: referrerCode } : data;

  return apiInstance.post("/sign-up", params);
};

/**
 * To get back password
 * @param email
 */
const forgetPassword = email =>
  apiInstance.post("/password/forget", { email: email });

/**
 * To login with facebook
 * @param fbAccessToken
 */
const loginWithFacebook = fbAccessToken =>
  apiInstance.post("/facebook", { token: fbAccessToken });

/**
 * To login account
 * @param mobileNumber
 * @param password
 */
const loginAccount = (mobileNumber, password) =>
  apiInstance.post(
    "/sign-in",
    { data: "token" },
    {
      auth: {
        username: mobileNumber,
        password: password
      }
    }
  );

/**
 * To get stall list
 * @param pageNumber
 * @param selectedFilterCategory
 * @param foodTagData
 * @param maxPriceData
 * @param minPriceData
 * @param maxEtaData
 */
const getStallList = (
  pageNumber,
  selectedFilterCategory,
  foodTagData,
  minPriceData,
  maxPriceData,
  maxEtaData
) => {
  const queryMultipleCategory = !_.isEmpty(selectedFilterCategory)
    ? `&category_id=${getArrayQueryParam(selectedFilterCategory)}`
    : "";
  const queryMinPrice = minPriceData ? `&min_price=${minPriceData}` : "";
  const queryMaxPrice = maxPriceData ? `&max_price=${maxPriceData}` : "";
  const queryMaxEta = maxEtaData ? `&max_eta=${maxEtaData}` : "";
  const queryFoodTag = foodTagData ? `&food_tag=${foodTagData}` : "";
  const queryPageNumber = pageNumber ? `?page=${pageNumber}` : "";

  return apiInstance.get(
    `/stall${queryPageNumber}${queryMultipleCategory}${queryMinPrice}${queryMaxPrice}${queryMaxEta}${queryFoodTag}`
  );
};

const getArrayQueryParam = array => {
  let params = "";

  for (let i = 0; i < array.length; i++) {
    if (i === 0) {
      params = params + array[i];
    } else {
      params = params + "," + array[i];
    }
  }

  return params;
};

/**
 * To get category list
 * @param pageNumber
 */
const getCategoryList = pageNumber =>
  apiInstance.get(`/category${pageNumber ? `?page=${pageNumber}` : ""}`);

/**
 * To retrieve the Stall details
 * @param stallID
 */
const getStallDetail = stallID => apiInstance.get(`/stall/${stallID}`);

/**
 * To get all the active stalls food
 * @param stallID
 * @param pageNumber
 * @param minPrice
 * @param maxPrice
 * @param maxEta
 * @param foodTag
 */
const getStallFoodData = (
  stallID,
  pageNumber = 1,
  minPrice,
  maxPrice,
  maxEta,
  foodTag
) => {
  const queryMinPrice = minPrice ? `&min_price=${minPrice}` : "";
  const queryMaxPrice = maxPrice ? `&max_price=${maxPrice}` : "";
  const queryMaxEta = maxEta ? `&max_eta=${maxEta}` : "";
  const queryMultipleFoodTag = !_.isEmpty(foodTag)
    ? `&tag_id=${getArrayQueryParam(foodTag)}`
    : "";
  return apiInstance.get(
    `/food${pageNumber ? `?page=${pageNumber}` : ""}${
      stallID ? `&stall_id=${stallID}` : ""
    }${queryMinPrice}${queryMaxPrice}${queryMaxEta}${queryMultipleFoodTag}`
  );
};

/**
 * To Request for verification code to sign up
 * @param phoneData
 */
const requestPhoneVerificationCode = phoneData =>
  apiInstance.post(`/verification`, phoneData);

/**
 * To Verify the code to authenticate phone number
 * @param phoneData
 */
const verifyPhoneVerificationCode = phoneData =>
  apiInstance.put(`/verification`, phoneData);

/**
 * To Retrieve the Food details
 * @param foodID
 */
const getFoodDetailItem = foodID => apiInstance.get(`/food/${foodID}`);

/**
 * To Retrieve the Food Reviews
 * @param foodID
 * @param pageNumber
 */
const getFoodReviewList = (foodID, pageNumber) =>
  apiInstance.get(
    `/food/${foodID}/review${pageNumber ? `?page=${pageNumber}` : ""}`
  );

/**
 * To Favorite the food
 * @param foodID
 */
const favouriteFood = foodID => apiInstance.post(`/food/${foodID}/favorite`);

/**
 * To UnFavorite the food
 * @param foodID
 */
const unFavouriteFood = foodID =>
  apiInstance.delete(`/food/${foodID}/favorite`);

/**
 * To Get all the favorite food of the user
 * @param pageNumber
 */
const getFavouriteFoodList = pageNumber =>
  apiInstance.get(`/favorite/food${pageNumber ? `?page=${pageNumber}` : ""}`);

/**
 * To Create a new Top Up request
 * @param topUpData
 */
const createNewTopUp = topUpData =>
  apiInstance.post(`/wallet/top-up`, topUpData);

/**
 * To Retrieve the Wallet details
 */
const getMyWalletDetail = () => apiInstance.get(`wallet`);

/**
 * Refresh the Wallet details cache
 */
const refreshWalletDetailCache = () => apiInstance.post(`wallet`);

/**
 * To Get User Cart Item
 */
const getUserCartItem = () => apiInstance.get(`/user/cart`);

/**
 * To Delete User Cart Item
 * @param cartID
 */
const deleteUserCartItem = cartID => apiInstance.delete(`/user/cart/${cartID}`);

/**
 * To apply wallet in this order
 * @param data
 */
const applyUserCartWallet = data => apiInstance.post(`/user/cart/wallet`, data);

/**
 * To apply point in this order
 * @param data
 */
const applyUserCartPoint = data => apiInstance.post(`/user/cart/point`, data);

/**
 * To add user cart food
 * @param foodID
 * @param foodData
 */
const addUserCartFood = (foodID, foodData) =>
  apiInstance.post(`user/cart/food/${foodID}`, foodData);

/**
 * To Update the food Cart
 * @param cartID
 * @param foodData
 */
const updateUserCartFood = (cartID, foodData) =>
  apiInstance.put(`user/cart/${cartID}`, foodData);

/**
 * To Update the food Cart delivery
 * @param cartID
 */
const updateUserCartFoodDelivery = cartID =>
  apiInstance.put(`user/cart/${cartID}/delivery`);

/**
 * To Checkout user cart
 * @param checkoutData
 */
const checkoutUserCart = checkoutData =>
  apiInstance.post(`user/cart/checkout`, checkoutData);

/**
 * To Get user order detail
 * @param orderID
 */
const getUserOrderDetail = orderID => apiInstance.get(`user/order/${orderID}`);

/**
 * Set or Update the order delivery table
 * @param tableData
 */
const updateUserOrderTable = tableData =>
  apiInstance.put(`user/cart/table`, tableData);

/**
 * Get all the user's Order Object
 * @param pageNumber
 */
const getUserOrderList = pageNumber =>
  apiInstance.get(`user/order${pageNumber ? `?page=${pageNumber}` : ""}`);

const cancelUserOrder = orderID => apiInstance.delete(`user/order/${orderID}`);

/**
 * To update order payment
 * @param paymentID
 * @param data
 */
const updateUserOrderPayment = (paymentID, data) =>
  apiInstance.put(`user/order/payment/${paymentID}`, data);

const postSubmitReview = (reviewID, data) =>
  apiInstance.post(`/review/${reviewID}`, data);

const getMeData = () => apiInstance.get(`/me`);

const postNewSearch = keyword =>
  apiInstance.post(`/search`, { keyword: keyword });

const putSearchQuery = query => apiInstance.put(`/search`, { query: query });

const updateProfileData = data => apiInstance.put(`/profile`, data);

const getUserQueueItem = () => apiInstance.get(`user/queue`);

const getUserQueueReadyItem = () => apiInstance.get(`user/queue/ready`);

const getUserQueueCompleteItem = () => apiInstance.get(`user/queue/complete`);

const postUserOrderPayment = (orderID, data) =>
  apiInstance.post(`user/order/${orderID}/payment`, data);

const postIpayPaymentRequest = (orderID, data) =>
  apiInstance.post(`user/order/${orderID}/payment`, data);

/**
 * Request to Reset Password
 * @param phoneData
 */
const requestForgetPassword = phoneData =>
  apiInstance.post(`password/forget`, phoneData);

/**
 * Reset Password using the password reset token within the expiry of the token
 * @param resetData
 */
const requestResetPassword = resetData =>
  apiInstance.post(`password/reset`, resetData);

const updateWalletTopUpPayment = (paymentID, data) =>
  apiInstance.put(`wallet/top-up/payment/${paymentID}`, data);

const getWalletTransactionList = pageNumber =>
  apiInstance.get(`/wallet/transaction?page=${pageNumber}`);

const getPointTransactionList = pageNumber =>
  apiInstance.get(`/point/transaction?page=${pageNumber}`);

const getPageTncData = () => apiInstance.get(`/page/tnc`);

/**
 * Get and Update Notification for food
 */
const getNotification = () => apiInstance.get(`/user/queue/web`);

const updateNotification = id => apiInstance.put(`/user/queue/web/${id}`);

const updateNotificationItemsID = itemsID =>
  apiInstance.post(`/user/queue/web/batch`, { ids: itemsID });

/**
 * Change Order Summary Table
 */
const updateOrderSummaryTable = (orderID, tableData) =>
  apiInstance.put(`/user/order/${orderID}/table`, tableData);

/**
 * Delivery Enabled
 */
const getCommonConfig = () => apiInstance.get(`/common`);

/**
 * Delivery Enabled
 */
const getOccupation = () => apiInstance.get(`/common/occupation`);

/**
 * Get Profile Details
 */
const getProfileBasic = () => apiInstance.get(`/profile/basic`);

/**
 * Update Profile Details
 */
const updateProfileBasic = data => apiInstance.put(`/profile/basic`, data);

/**
 * Link Scocial
 */
const linkSocial = (channel, fbAccessToken) =>
  apiInstance.post(`/profile/social`, {
    channel: channel,
    access_token: fbAccessToken
  });

/**
 * Get Profile Details
 */
const getAppPopUp = () => apiInstance.get(`/banner?code=app-popup`);

export default {
  loginAccount,
  forgetPassword,
  setHeaderToken,
  signUpAccount,
  setHeaderLanguage,
  getStallList,
  getCategoryList,
  getStallDetail,
  getStallFoodData,
  requestPhoneVerificationCode,
  verifyPhoneVerificationCode,
  loginWithFacebook,
  favouriteFood,
  unFavouriteFood,
  getFavouriteFoodList,
  getFoodReviewList,
  getFoodDetailItem,
  createNewTopUp,
  getMyWalletDetail,
  refreshWalletDetailCache,
  getUserCartItem,
  deleteUserCartItem,
  applyUserCartWallet,
  applyUserCartPoint,
  addUserCartFood,
  updateUserCartFood,
  updateUserCartFoodDelivery,
  checkoutUserCart,
  getUserOrderDetail,
  updateUserOrderTable,
  updateUserOrderPayment,
  postSubmitReview,
  getUserOrderList,
  cancelUserOrder,
  getMeData,
  updateProfileData,
  postNewSearch,
  putSearchQuery,
  postUserOrderPayment,
  requestForgetPassword,
  requestResetPassword,
  getUserQueueItem,
  getUserQueueReadyItem,
  getUserQueueCompleteItem,
  updateWalletTopUpPayment,
  getWalletTransactionList,
  getPointTransactionList,
  getPageTncData,
  getNotification,
  updateNotification,
  updateNotificationItemsID,
  updateOrderSummaryTable,
  getCommonConfig,
  getOccupation,
  getProfileBasic,
  updateProfileBasic,
  linkSocial,
  getAppPopUp
};



// WEBPACK FOOTER //
// ./src/services/httpUtilities/httpService.js